// Compliance-Watchdog: eigener Toolbar-Reiter neben Marketing. Zeigt den
// täglichen Abmahn-Begriff-Check (om_compliance_reports) je Portal, erlaubt
// manuelles Auslösen und setzt ein Menü-Badge mit der Treffer-Anzahl.
// Datenquelle: claude_compliance_watch.mjs → om_compliance_reports + om_jobs.
import type { MarketingDeps } from "./marketing.js";

// Watchdog braucht zusätzlich den aktiven Projekt-Slug (für "Direkt fixen" → tmux-Session).
type WatchdogDeps = MarketingDeps & { currentSlug: () => string | null };

interface WdItem { url: string; status: number; phrase: string; schwere: string; grund: string; snippet: string; match?: string }
interface WdPortal { portal: string; checked: number; findings: number; items: WdItem[] }
interface WdSummary { run_stamp: string; generated_at: string; terms: string[]; portals: WdPortal[]; total_findings: number }
interface WdReports {
  latest: { id: number; runStamp: string; createdAt: string; status: string; hasMarkdown: boolean; summary: WdSummary } | null;
  history: Array<{ id: number; runStamp: string; createdAt: string; status: string }>;
}
interface WdJobs { jobs: Array<{ id: number; type: string; status: string; started_at: string; finished_at: string | null; log: string | null }> }
interface Term { phrase: string; schwere: string; grund: string; aktiv: boolean; ausnahmen: string[] }
interface TermsResponse { terms: Term[]; whitelist: string[] }

// Stabiler Schlüssel eines Funds (url + Begriff) — für die dauerhafte Ignore-Liste.
const ignKey = (url: string, phrase: string): string => `${url} ${phrase}`;
async function fetchIgnores(deps: MarketingDeps): Promise<Set<string>> {
  try {
    const r = await deps.api<{ ignores: Array<{ url: string; phrase: string }> }>("/api/marketing/compliance-ignores");
    return new Set((r.data?.ignores ?? []).map((i) => ignKey(i.url, i.phrase)));
  } catch { return new Set<string>(); }
}

// Editor für die pflegbare Verbots-Wortliste (marketing_compliance_terms.json).
async function initTermsEditor(m: HTMLElement, deps: MarketingDeps): Promise<void> {
  const { api } = deps;
  const listHost = m.querySelector("[data-terms-list]") as HTMLElement | null;
  const whiteEl = m.querySelector("[data-terms-white]") as HTMLTextAreaElement | null;
  const msg = m.querySelector('[data-msg="terms"]') as HTMLElement | null;
  if (!listHost || !whiteEl || !msg) return;

  const field = (tag: string, cls: string, attrs: Record<string, string> = {}): HTMLElement => {
    const el = document.createElement(tag);
    el.className = cls;
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    return el;
  };

  const addRow = (t?: Partial<Term>): void => {
    const row = field("div", "om-wd-term");
    const row1 = field("div", "row1");
    const phrase = field("input", "f-phrase", { type: "text", "data-f": "phrase", placeholder: "z. B. staatlich geprüft" }) as HTMLInputElement;
    phrase.value = t?.phrase ?? "";
    const schwere = field("select", "f-schwere", { "data-f": "schwere" }) as HTMLSelectElement;
    for (const s of ["kritisch", "hoch", "mittel"]) {
      const o = document.createElement("option"); o.value = s; o.textContent = s;
      if ((t?.schwere ?? "kritisch") === s) o.selected = true;
      schwere.appendChild(o);
    }
    const aktivLabel = field("label", "chk");
    const aktiv = field("input", "", { type: "checkbox", "data-f": "aktiv" }) as HTMLInputElement;
    aktiv.checked = t?.aktiv !== false;
    aktivLabel.append(aktiv, document.createTextNode(" aktiv"));
    const del = field("button", "del", { type: "button" }); del.textContent = "Löschen";
    del.addEventListener("click", () => row.remove());
    row1.append(phrase, schwere, aktivLabel, del);

    const grund = field("input", "f-grund", { type: "text", "data-f": "grund", placeholder: "Grund / Notiz (optional)" }) as HTMLInputElement;
    grund.value = t?.grund ?? "";
    const ausn = field("textarea", "f-ausnahmen", { "data-f": "ausnahmen", rows: "2", placeholder: "Ausnahmen — eine pro Zeile (optional)" }) as HTMLTextAreaElement;
    ausn.value = (t?.ausnahmen ?? []).join("\n");

    row.append(row1, grund, ausn);
    listHost.appendChild(row);
  };

  const load = async (): Promise<void> => {
    const r = await api<TermsResponse>("/api/marketing/compliance-terms");
    listHost.textContent = "";
    for (const t of r.data?.terms ?? []) addRow(t);
    whiteEl.value = (r.data?.whitelist ?? []).join("\n");
  };

  m.querySelector('[data-action="add-term"]')?.addEventListener("click", () => addRow());
  m.querySelector('[data-action="save-terms"]')?.addEventListener("click", () => {
    void (async () => {
      const terms = Array.from(listHost.querySelectorAll(".om-wd-term")).map((row) => ({
        phrase: (row.querySelector('[data-f="phrase"]') as HTMLInputElement).value.trim(),
        schwere: (row.querySelector('[data-f="schwere"]') as HTMLSelectElement).value,
        grund: (row.querySelector('[data-f="grund"]') as HTMLInputElement).value.trim(),
        aktiv: (row.querySelector('[data-f="aktiv"]') as HTMLInputElement).checked,
        ausnahmen: (row.querySelector('[data-f="ausnahmen"]') as HTMLTextAreaElement).value.split("\n").map((x) => x.trim()).filter(Boolean),
      })).filter((t) => t.phrase);
      const whitelist = whiteEl.value.split("\n").map((x) => x.trim()).filter(Boolean);
      msg.textContent = "Speichere …";
      const r = await api<{ ok?: boolean; error?: string }>("/api/marketing/compliance-terms", "PUT", { terms, whitelist });
      msg.textContent = r.status === 200
        ? `✅ ${terms.length} Begriffe gespeichert — gilt ab dem nächsten Lauf.`
        : `Fehler (${r.status})`;
    })();
  });

  await load();
}

async function refreshWatchdogBadge(deps: MarketingDeps): Promise<void> {
  try {
    const [r, ign] = await Promise.all([
      deps.api<WdReports>("/api/marketing/compliance-reports"),
      fetchIgnores(deps),
    ]);
    // Badge = Treffer im letzten Report MINUS dauerhaft ignorierte Funde.
    let n = 0;
    for (const p of r.data?.latest?.summary?.portals ?? []) {
      for (const it of p.items) if (!ign.has(ignKey(it.url, it.phrase))) n++;
    }
    const b = document.getElementById("watchdog-badge");
    if (b) { b.textContent = String(n); b.hidden = n === 0; }
  } catch { /* Badge ist nice-to-have, Fehler ignorieren */ }
}

function renderWdSummary(host: HTMLElement, s: WdSummary, deps: WatchdogDeps, ignoreSet: Set<string>, onChange: () => void): void {
  const formatDate = deps.formatDate;
  host.textContent = "";

  // Dauerhaft ignorierte Funde ("✕ Nicht beachten") ausblenden; alle Zähler
  // (Chips, Kopfzeile, Portal-Überschrift) rechnen mit den SICHTBAREN Treffern.
  const visItemsOf = (p: WdPortal): WdItem[] => p.items.filter((it) => !ignoreSet.has(ignKey(it.url, it.phrase)));
  const visByPortal = new Map<string, WdItem[]>(s.portals.map((p) => [p.portal, visItemsOf(p)]));
  const total = [...visByPortal.values()].reduce((acc, arr) => acc + arr.length, 0);

  // Portal-Filter (sticky) — Chips werden DYNAMISCH aus s.portals erzeugt (Alle + je Portal im Report,
  // inkl. DAFMP/DAFMF), kein fester Allow-List. Neue Portale erscheinen automatisch.
  const filters = document.createElement("div");
  filters.className = "om-wd-filters";
  const boxes: HTMLElement[] = [];
  const chips: HTMLButtonElement[] = [];
  let active = "__all";
  const applyFilter = (): void => {
    for (const b of boxes) b.hidden = !(active === "__all" || b.dataset.portal === active);
    for (const c of chips) c.classList.toggle("active", c.dataset.pf === active);
  };
  const mkChip = (label: string, key: string, hits: number): void => {
    const b = document.createElement("button");
    b.className = "om-wd-chip" + (hits > 0 ? " has-hits" : "");
    b.textContent = label;
    b.dataset.pf = key;
    b.addEventListener("click", () => { active = key; applyFilter(); });
    chips.push(b);
    filters.appendChild(b);
  };
  mkChip(`Alle (${total})`, "__all", total);
  for (const p of s.portals) { const v = visByPortal.get(p.portal)!.length; mkChip(`${p.portal} (${v})`, p.portal, v); }
  host.appendChild(filters);

  const head = document.createElement("p");
  head.className = total > 0 ? "om-wd-head alert" : "om-wd-head clean";
  const when = s.generated_at ? formatDate(s.generated_at) : s.run_stamp;
  head.textContent = total > 0
    ? `⚠️ ${total} Treffer — geprüft ${when} · Begriffe: ${s.terms.join(", ")}`
    : `✅ Keine Treffer — geprüft ${when} · Begriffe: ${s.terms.join(", ")}`;
  host.appendChild(head);

  for (const p of s.portals) {
    const box = document.createElement("div");
    box.className = "om-wd-portal";
    box.dataset.portal = p.portal;
    boxes.push(box);
    const visItems = visByPortal.get(p.portal)!;
    const h = document.createElement("h3");
    h.textContent = `${p.portal} — ${p.checked} Seiten geprüft · ${visItems.length} Treffer`;
    if (visItems.length > 0) h.classList.add("alert");
    box.appendChild(h);
    if (visItems.length) {
      const ul = document.createElement("ul");
      ul.className = "om-wd-list";
      for (const it of visItems.slice(0, 120)) {
        const li = document.createElement("li");

        // Kopf: Begriff-Badge + deutlich klickbarer Link (öffnet die Seite im neuen Tab)
        const headEl = document.createElement("div");
        headEl.className = "om-wd-item-head";
        const badge = document.createElement("span");
        badge.className = "om-wd-phrase";
        badge.textContent = it.phrase;
        const a = document.createElement("a");
        a.className = "om-wd-link";
        a.href = it.url; a.target = "_blank"; a.rel = "noopener";
        a.textContent = "↗ " + (it.url.replace(/^https?:\/\/[^/]+/, "") || it.url);
        headEl.append(badge, a);

        // Kontext-Satz mit gelb markiertem Fundwort → man sieht sofort WO es steht
        const snip = document.createElement("div");
        snip.className = "om-wd-snip";
        const hay = it.snippet || "";
        const needle = it.match || it.phrase;
        const pos = needle ? hay.toLowerCase().indexOf(needle.toLowerCase()) : -1;
        if (pos >= 0) {
          const mark = document.createElement("mark");
          mark.textContent = hay.substr(pos, needle.length);
          snip.append(document.createTextNode("…" + hay.slice(0, pos)), mark, document.createTextNode(hay.slice(pos + needle.length) + "…"));
        } else {
          snip.textContent = "…" + hay + "…";
        }

        li.append(headEl, snip);
        if (it.grund) {
          const g = document.createElement("div");
          g.className = "om-wd-grund";
          g.textContent = "Grund: " + it.grund;
          li.appendChild(g);
        }

        // Aktionen: "Nicht beachten" (nur wegblenden, kommt beim nächsten Lauf wieder)
        // + "Direkt fixen" (übergibt den Fund an die laufende Claude-Session).
        const actions = document.createElement("div");
        actions.className = "om-wd-actions";
        const ignore = document.createElement("button");
        ignore.className = "om-wd-btn ignore"; ignore.type = "button"; ignore.textContent = "✕ Nicht beachten";
        ignore.title = "Blendet den Eintrag dauerhaft aus — bleibt auch nach dem nächsten Lauf ignoriert";
        ignore.addEventListener("click", () => {
          void (async () => {
            ignore.disabled = true;
            const r = await deps.api<{ ok?: boolean; error?: string }>(
              "/api/marketing/compliance-ignores", "POST", { url: it.url, phrase: it.phrase },
            );
            if (r.status === 200) { ignoreSet.add(ignKey(it.url, it.phrase)); onChange(); }
            else { ignore.disabled = false; }
          })();
        });
        const fix = document.createElement("button");
        fix.className = "om-wd-btn fix"; fix.type = "button"; fix.textContent = "🔧 Direkt fixen";
        fix.title = "Übergibt den Fund an Claude im Terminal zur direkten Bearbeitung";
        const fmsg = document.createElement("span");
        fmsg.className = "om-wd-fmsg";
        fix.addEventListener("click", () => {
          void (async () => {
            const slug = deps.currentSlug();
            if (!slug) { fmsg.textContent = "Kein Projekt aktiv."; return; }
            fix.disabled = true; fmsg.textContent = "Übergebe an Claude …";
            const r = await deps.api<{ ok?: boolean; error?: string }>(`/api/projects/${slug}/compliance-fix`, "POST", { url: it.url, phrase: it.phrase, snippet: it.snippet });
            if (r.status === 200) {
              fmsg.textContent = "→ an Claude übergeben (siehe Terminal)";
              li.classList.add("om-wd-dispatched");
            } else if (r.status === 409 && r.data?.error === "claude_not_running") {
              fmsg.textContent = "Bitte zuerst Claude Code starten.";
              fix.disabled = false;
            } else if (r.status === 409) {
              fmsg.textContent = "Terminal nicht bereit.";
              fix.disabled = false;
            } else {
              fmsg.textContent = `Fehler (${r.status})`;
              fix.disabled = false;
            }
          })();
        });
        actions.append(ignore, fix, fmsg);
        li.appendChild(actions);

        ul.appendChild(li);
      }
      box.appendChild(ul);
    }
    host.appendChild(box);
  }
  applyFilter();
}

async function openWatchdogModal(deps: WatchdogDeps): Promise<void> {
  const { api, openModal, formatDate } = deps;
  const m = openModal("tpl-watchdog-modal");
  const summaryHost = m.querySelector("[data-wd-summary]") as HTMLElement;
  const historySel = m.querySelector("[data-wd-history]") as HTMLSelectElement;
  const msg = m.querySelector('[data-msg="watchdog"]') as HTMLElement;
  let pollTimer: number | undefined;

  // Dauerhaft ignorierte Funde + zuletzt geladener Report; onChange rendert neu
  // (nach "Nicht beachten") und zieht das Menü-Badge nach.
  const ignoreSet = new Set<string>();
  let latestSummary: WdSummary | null = null;
  const onChange = (): void => {
    if (latestSummary) renderWdSummary(summaryHost, latestSummary, deps, ignoreSet, onChange);
    void refreshWatchdogBadge(deps);
  };

  void initTermsEditor(m, deps);

  const loadReports = async (): Promise<void> => {
    const [r, ign] = await Promise.all([
      api<WdReports>("/api/marketing/compliance-reports"),
      fetchIgnores(deps),
    ]);
    const data = r.data;
    if (!data) return;
    ignoreSet.clear();
    for (const k of ign) ignoreSet.add(k);
    historySel.textContent = "";
    for (const h of data.history) {
      const o = document.createElement("option");
      o.value = String(h.id);
      o.textContent = `${h.runStamp} (${h.status})`;
      historySel.appendChild(o);
    }
    if (data.latest) {
      latestSummary = data.latest.summary;
      renderWdSummary(summaryHost, latestSummary, deps, ignoreSet, onChange);
    }
  };

  m.querySelector('[data-action="open-wd-report"]')?.addEventListener("click", () => {
    const id = historySel.value;
    if (id) window.open(`/api/marketing/compliance-reports/${id}/markdown`, "_blank");
  });

  m.querySelector('[data-action="run-watchdog"]')?.addEventListener("click", () => {
    void (async () => {
      msg.textContent = "Starte Check …";
      const r = await api<{ ok?: boolean; stamp?: string; error?: string }>(
        "/api/marketing/motors/compliance_watch/run", "POST",
      );
      if (r.status === 409) { msg.textContent = "Läuft bereits."; return; }
      if (r.status !== 200) { msg.textContent = `Fehler (${r.status})`; return; }
      msg.textContent = `Läuft (${r.data?.stamp}) — dauert ~2–3 Min, Ergebnis erscheint hier automatisch.`;
      const poll = async (): Promise<void> => {
        const jr = await api<WdJobs>("/api/marketing/jobs?type=compliance_watch");
        const finished = jr.data?.jobs.find((j) => j.status !== "running");
        const running = jr.data?.jobs.some((j) => j.status === "running");
        if (!running && finished) {
          msg.textContent = finished.status === "done"
            ? `Fertig: ${formatDate(finished.finished_at ?? finished.started_at)}`
            : `Fehlgeschlagen: ${finished.log ?? ""}`;
          await loadReports();
          void refreshWatchdogBadge(deps);
          return;
        }
        if (m.isConnected) pollTimer = window.setTimeout(() => { void poll(); }, 5000);
      };
      pollTimer = window.setTimeout(() => { void poll(); }, 5000);
    })();
  });

  m.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).classList.contains("modal-backdrop")) window.clearTimeout(pollTimer);
  });

  await loadReports();
}

export function setupWatchdog(deps: WatchdogDeps): void {
  const btn = document.getElementById("watchdog-btn");
  if (!btn) return;
  btn.addEventListener("click", () => { void openWatchdogModal(deps); });
  void refreshWatchdogBadge(deps);
  // Badge selbstständig nachziehen (nach Cron-/CLI-Läufen, ohne Seiten-Reload)
  window.setInterval(() => { void refreshWatchdogBadge(deps); }, 120_000);
}
